import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';

// ─── helpers ────────────────────────────────────────────────────────────────

const Toast = ({ msg, onClose }) => (
  <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-xl animate-fade-in">
    <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    <span className="text-sm font-medium">{msg}</span>
    <button onClick={onClose} className="ml-2 text-gray-400 hover:text-white">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const useToast = () => {
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  return { toast, showToast };
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AdminSelfPaced = () => {
  const { toast, showToast } = useToast();

  // Courses list
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected course detail
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Students list (for access management)
  const [allStudents, setAllStudents] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');

  // Modals
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [editModule, setEditModule] = useState(null);

  // Forms
  const [courseForm, setCourseForm] = useState({ title: '', description: '' });
  const [moduleForm, setModuleForm] = useState({ title: '', youtube_link: '', order: 0 });
  const [saving, setSaving] = useState(false);

  // ── fetch all courses ──────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/api/selfpaced');
      setCourses(res.data || []);
    } catch {
      setError('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── fetch course detail ────────────────────────────────────────────────────
  const fetchDetail = useCallback(async (courseId) => {
    setDetailLoading(true);
    try {
      const [detailRes, studentsRes] = await Promise.all([
        api.get(`/api/selfpaced/${courseId}`),
        api.get(`/api/selfpaced/${courseId}/students`),
      ]);
      setDetail(detailRes.data);
      setCourseStudents(studentsRes.data || []);
    } catch {
      showToast('Failed to load course details');
    } finally {
      setDetailLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── fetch all students ─────────────────────────────────────────────────────
  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get('/api/users?role=student&per_page=200');
      setAllStudents(res.data.users || res.data || []);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => { fetchCourses(); fetchStudents(); }, [fetchCourses, fetchStudents]);

  const handleSelectCourse = (course) => {
    setSelected(course);
    fetchDetail(course.id);
    setStudentSearch('');
  };

  // ── Course CRUD ────────────────────────────────────────────────────────────

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editCourse) {
        await api.put(`/api/selfpaced/${editCourse.id}`, courseForm);
        showToast('Course updated');
      } else {
        await api.post('/api/selfpaced', courseForm);
        showToast('Course created');
      }
      setCourseForm({ title: '', description: '' });
      setShowAddCourse(false);
      setEditCourse(null);
      fetchCourses();
      if (selected) fetchDetail(selected.id);
    } catch {
      showToast('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course and all its modules?')) return;
    try {
      await api.delete(`/api/selfpaced/${courseId}`);
      showToast('Course deleted');
      if (selected?.id === courseId) { setSelected(null); setDetail(null); }
      fetchCourses();
    } catch {
      showToast('Failed to delete course');
    }
  };

  // ── Module CRUD ────────────────────────────────────────────────────────────

  const handleSaveModule = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editModule) {
        await api.put(`/api/selfpaced/${selected.id}/modules/${editModule.id}`, moduleForm);
        showToast('Module updated');
      } else {
        await api.post(`/api/selfpaced/${selected.id}/modules`, moduleForm);
        showToast('Module added');
      }
      setModuleForm({ title: '', youtube_link: '', order: 0 });
      setShowAddModule(false);
      setEditModule(null);
      fetchDetail(selected.id);
      fetchCourses();
    } catch {
      showToast('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await api.delete(`/api/selfpaced/${selected.id}/modules/${moduleId}`);
      showToast('Module deleted');
      fetchDetail(selected.id);
      fetchCourses();
    } catch {
      showToast('Failed to delete module');
    }
  };

  // ── Access management ──────────────────────────────────────────────────────

  const grantedIds = new Set(courseStudents.map((s) => s.student_id));

  const filteredStudents = allStudents.filter((s) => {
    if (!studentSearch) return true;
    const q = studentSearch.toLowerCase();
    return s.full_name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
  });

  const handleGrantAccess = async (studentId) => {
    try {
      await api.post(`/api/selfpaced/${selected.id}/access`, { student_id: studentId });
      showToast('Access granted');
      fetchDetail(selected.id);
      fetchCourses();
    } catch {
      showToast('Failed to grant access');
    }
  };

  const handleRevokeAccess = async (studentId) => {
    try {
      await api.delete(`/api/selfpaced/${selected.id}/access/${studentId}`);
      showToast('Access revoked');
      fetchDetail(selected.id);
      fetchCourses();
    } catch {
      showToast('Failed to revoke access');
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="skeleton h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast msg={toast} onClose={() => {}} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(32,201,151,0.12)' }}>
              <svg className="w-5 h-5" style={{ color: '#20C997' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Self-Paced Courses</h1>
              <p className="text-sm text-gray-500">{courses.length} course{courses.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={() => { setCourseForm({ title: '', description: '' }); setEditCourse(null); setShowAddCourse(true); }}
            className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg,#20C997,#003479)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Course
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ── Course list panel ── */}
          <div className="lg:col-span-2 space-y-3">
            {courses.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400">
                <p className="text-sm font-medium">No courses yet</p>
                <p className="text-xs mt-1">Create your first self-paced course above</p>
              </div>
            ) : (
              courses.map((course) => (
                <div
                  key={course.id}
                  onClick={() => handleSelectCourse(course)}
                  className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all hover:shadow-sm ${
                    selected?.id === course.id
                      ? 'border-[#20C997] shadow-sm ring-1 ring-[#20C997]/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{course.title}</p>
                      {course.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{course.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {course.module_count} module{course.module_count !== 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {course.student_count} student{course.student_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setCourseForm({ title: course.title, description: course.description || '' }); setEditCourse(course); setShowAddCourse(true); }}
                        className="p-1.5 text-gray-400 hover:text-[#003479] hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteCourse(course.id); }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Course detail panel ── */}
          <div className="lg:col-span-3">
            {!selected ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-300 h-64 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                  </svg>
                  <p className="text-sm font-medium">Select a course to manage</p>
                </div>
              </div>
            ) : detailLoading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                {[1, 2, 3].map((i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
              </div>
            ) : detail ? (
              <div className="space-y-4">
                {/* Course header */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <h2 className="text-lg font-bold text-gray-900">{detail.title}</h2>
                  {detail.description && <p className="text-sm text-gray-500 mt-1">{detail.description}</p>}
                </div>

                {/* Modules section */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">
                      Modules
                      <span className="ml-2 text-xs font-normal text-gray-500">{detail.modules?.length || 0}</span>
                    </h3>
                    <button
                      onClick={() => { setModuleForm({ title: '', youtube_link: '', order: (detail.modules?.length || 0) }); setEditModule(null); setShowAddModule(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-all hover:opacity-90"
                      style={{ background: '#20C997' }}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Module
                    </button>
                  </div>

                  {!detail.modules || detail.modules.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">
                      <p className="text-sm">No modules yet — add your first one</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {detail.modules.map((mod, idx) => (
                        <div key={mod.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/60 transition-colors">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: '#003479' }}>
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{mod.title}</p>
                            <a
                              href={mod.youtube_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#20C997] hover:underline truncate block"
                            >
                              {mod.youtube_link}
                            </a>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => { setModuleForm({ title: mod.title, youtube_link: mod.youtube_link, order: mod.order }); setEditModule(mod); setShowAddModule(true); }}
                              className="p-1.5 text-gray-400 hover:text-[#003479] hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteModule(mod.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Student access section */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">
                      Student Access
                      <span className="ml-2 text-xs font-normal text-gray-500">{courseStudents.length} enrolled</span>
                    </h3>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Search students */}
                    <div className="relative">
                      <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        placeholder="Search students..."
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                      />
                    </div>

                    {/* Student list */}
                    <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 rounded-xl border border-gray-100">
                      {filteredStudents.length === 0 ? (
                        <p className="py-4 text-center text-xs text-gray-400">No students found</p>
                      ) : (
                        filteredStudents.map((student) => {
                          const hasAccess = grantedIds.has(student.id);
                          return (
                            <div key={student.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{student.full_name}</p>
                                <p className="text-xs text-gray-500">{student.email}</p>
                              </div>
                              <button
                                onClick={() => hasAccess ? handleRevokeAccess(student.id) : handleGrantAccess(student.id)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                  hasAccess
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                    : 'text-white hover:opacity-90'
                                }`}
                                style={!hasAccess ? { background: '#20C997' } : {}}
                              >
                                {hasAccess ? 'Revoke' : 'Grant'}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Add/Edit Course Modal ── */}
      {showAddCourse && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editCourse ? 'Edit Course' : 'New Course'}</h2>
              <button onClick={() => { setShowAddCourse(false); setEditCourse(null); }} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveCourse} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="e.g. Python for Beginners"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm resize-none focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="Brief description of the course..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowAddCourse(false); setEditCourse(null); }} className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 text-white text-sm font-semibold rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#20C997,#003479)' }}
                >
                  {saving ? 'Saving…' : editCourse ? 'Update' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add/Edit Module Modal ── */}
      {showAddModule && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editModule ? 'Edit Module' : 'Add Module'}</h2>
              <button onClick={() => { setShowAddModule(false); setEditModule(null); }} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveModule} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Module Title *</label>
                <input
                  type="text"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="e.g. Introduction to Variables"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Link *</label>
                <input
                  type="url"
                  value={moduleForm.youtube_link}
                  onChange={(e) => setModuleForm({ ...moduleForm, youtube_link: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input
                  type="number"
                  min={0}
                  value={moduleForm.order}
                  onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowAddModule(false); setEditModule(null); }} className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 text-white text-sm font-semibold rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#20C997,#003479)' }}
                >
                  {saving ? 'Saving…' : editModule ? 'Update Module' : 'Add Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminSelfPaced;
